import React, { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { claimDripOffer } from '../../redux/actions/myAccountAction'
import { SETTING } from 'routes/routeConstant'
import { hapticSuccess } from 'utils/haptics'
import ClaimLanding from './ClaimLanding'

/**
 * /claim/trial-offer — one-click link for the drip emails that pitch the free
 * days AND the 50% offer in the same message (e.g. Track A Day 3). Nothing is
 * activated here: it claims the personal 50%-off-3-months voucher and lands on
 * the plan page in the trial-offer flow (&flow=trial-offer), where the page
 * lays out the journey — pick a plan (Standard or Pro, monthly), 5 days free,
 * then 50% off the first 3 months — and the checkout started from the cards
 * carries both the trial and the coupon. The flow param is set even when the
 * offer claim fails or is ineligible so the page can still explain the journey;
 * coupon/until are simply omitted then.
 */
const ClaimTrialOffer: React.FC = () => {
  const { t } = useTranslation('offers')
  const navigate = useNavigate()
  const hasRun = useRef(false)

  useEffect(() => {
    if (hasRun.current) return // guard the StrictMode double-mount — the claim is one-shot
    hasRun.current = true
    ;(async () => {
      let couponCode = ''
      let expiresAt = 0
      let trialDays = -1
      try {
        const res: any = await claimDripOffer()
        if (res?.eligible && typeof res?.code === 'string' && res.code.length > 0) {
          couponCode = res.code
          if (typeof res?.expiresAt === 'number' && isFinite(res.expiresAt) && res.expiresAt > 0) {
            expiresAt = Math.floor(res.expiresAt)
          }
          // Server-decided entitlement (trial history): 5 = never trialled
          // (free days + 50%), 0 = ever trialled (50% only, no second trial).
          if (typeof res?.trialDays === 'number') trialDays = res.trialDays
          hapticSuccess()
        }
      } catch (e) {
        // fall through to the plan page — the journey still gets explained
      }

      const params = new URLSearchParams()
      if (couponCode) params.set('coupon', couponCode)
      if (couponCode && expiresAt) params.set('until', String(expiresAt))
      if (trialDays >= 0) params.set('trial', String(trialDays))
      params.set('flow', 'trial-offer')
      navigate(
        {
          pathname: SETTING,
          search: `?${params.toString()}`,
          hash: '#plan',
        },
        { replace: true },
      )
    })()
  }, [])

  return <ClaimLanding message={t('claim.preparingOffer')} />
}

export default ClaimTrialOffer
